import { Button } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { useMutation } from "@tanstack/react-query";
import { useExportablesContext } from "@/components/AIComponents/ExportablesModal/ExportablesContextProvider/useExportablesContext";
import api, { ApiCatalog } from "@/api";
import buildGraphicOrganizerResponsePayload from "@/view/utils/buildGraphicOrganizerResponsePayload/buildGraphicOrganizerResponsePayload";
import {
  hideOnMobileCss,
  endAlignedButtonCss,
} from "./ExportablesModalFooter.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

const StudentActions = () => {
  const {
    content,
    formData,
    isStudent,
    selectedAssignmentId,
    selectedExportable,
    templateList,
    handleTemplateSelection,
    handleModalClose,
    renderOnMobile,
  } = useExportablesContext();
  const { mutateAsync: postGraphicOrganizerResponse } = useMutation({
    mutationFn: async (payload: any) => {
      await api().post(ApiCatalog.graphicOrganizerResponses.endpoint, payload);
    },
  });

  const currentTemplateIndex =
    templateList?.findIndex(
      ({ seti_identifier }) =>
        seti_identifier === selectedExportable.seti_identifier,
    ) || 0;
  const isFirstTemplate = currentTemplateIndex === 0;
  const isLastTemplate = currentTemplateIndex === templateList.length - 1;

  const submitStudentResponse = async () => {
    if (!isStudent) return;

    try {
      const payload = buildGraphicOrganizerResponsePayload({
        assignmentId: selectedAssignmentId,
        contentId: content.id,
        formData,
        selectedExportable,
      });

      await postGraphicOrganizerResponse(payload);
    } catch (error) {
      console.error("Error submitting graphic organizer response:", error);
    }
  };

  const handleNextButton = async () => {
    await submitStudentResponse();
    if (isLastTemplate) {
      handleModalClose();
      return;
    }
    handleTemplateSelection(
      templateList[Math.min(currentTemplateIndex + 1, templateList.length - 1)],
    );
  };

  const handlePreviousButton = async () => {
    await submitStudentResponse();
    handleTemplateSelection(
      templateList[Math.max(currentTemplateIndex - 1, 0)],
    );
  };
  return (
    <>
      {!isFirstTemplate && (
        <div
          css={!renderOnMobile && hideOnMobileCss}
          data-testid="footer-button"
        >
          <ButtonWithTrackEvent
            flavor={Button.flavors.secondary}
            onClick={handlePreviousButton}
            dataProps={{
              "data-qa-selector": "show-previous-graphic-organizer",
            }}
            objectType={objectTypes.graphicOrganizerSubmission}
            actionPrefix={actionPrefixes.initiate}
            actionName={actionNames.navigate}
            streamProperties={{
              description: "Save and show previous Graphic Organizer",
            }}
          >
            Save and previous
          </ButtonWithTrackEvent>
        </div>
      )}
      <div
        css={[endAlignedButtonCss, !renderOnMobile && hideOnMobileCss]}
        data-testid="footer-button"
      >
        <ButtonWithTrackEvent
          onClick={handleNextButton}
          dataProps={{
            "data-qa-selector": isLastTemplate
              ? "save-graphic-organizer-submission"
              : "show-next-graphic-organizer",
          }}
          objectType={objectTypes.graphicOrganizerSubmission}
          actionPrefix={actionPrefixes.initiate}
          actionName={isLastTemplate ? actionNames.save : actionNames.navigate}
          streamProperties={{
            description: isLastTemplate
              ? "Save Graphic Organizer submission"
              : "Save and show next Graphic Organizer",
          }}
        >
          {isLastTemplate ? "Save and submit" : "Save and next"}
        </ButtonWithTrackEvent>
      </div>
    </>
  );
};

export default StudentActions;
